<template>
  <div class="shared-pluviometric-chart">
    <!-- Top toolbar: logo left, hamburger menu right -->
    <header class="shared-toolbar">
      <a href="https://info.ponceag.com/auditorias" target="_blank">
        <img
          class="shared-toolbar__logo"
          :src="require('../../assets/logo_black.png')"
          alt="PONCE"
          width="110"
        >
      </a>
      <v-menu
        v-model="hamburgerMenuOpen"
        :close-on-content-click="true"
        offset-y
        right
        nudge-bottom="20"
        content-class="hamburger-menu-content"
        transition="scale-transition"
        origin="top right"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            class="shared-toolbar__menu-btn"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-menu</v-icon>
          </v-btn>
        </template>
        <div class="hamburger-overlay">
          <v-btn
            text
            class="hamburger-overlay__item"
            @click="openAboutAudits"
          >
            <img :src="require('../../assets/logoPonce.png')" alt="PONCE" width="24" class="mr-1">
            Sobre PONCE Auditorías
          </v-btn>
          <v-btn
            text
            class="hamburger-overlay__item"
            @click="openAboutMonitoring"
          >
            <img :src="require('../../assets/logoPonce.png')" alt="PONCE" width="24" class="mr-1">
            Sobre PONCE Monitoreo
          </v-btn>
          <v-btn
            text
            class="hamburger-overlay__item"
            @click="openContactSupport"
          >
            <v-icon left color="#0098E1" class="ml-1">mdi-whatsapp</v-icon>
            Contactar a soporte
          </v-btn>
        </div>
      </v-menu>
    </header>

    <!-- Backdrop: transparent overlay when hamburger menu is open (below header) -->
    <v-overlay
      :value="hamburgerMenuOpen"
      :opacity="0.35"
      :z-index="8"
      @click.native="hamburgerMenuOpen = false"
    />

    <!-- Main content: white panel with PDF viewer and overlays over it -->
    <div
      v-if="pluviometricChartData && pluviometricChartData.file && pluviometricChartData.file.url"
      class="shared-viewer-panel"
    >
    <div class="shared-viewer-area">
        <PdfViewer :pdf-url="pluviometricChartData.file.url" />
        <!-- 3-dots button and overlay over the PDF viewer -->
        <div class="viewer-actions">
          <v-menu
            v-model="threeDotsMenuOpen"
            :close-on-content-click="true"
            offset-y
            left
            nudge-left="8"
            content-class="shared-viewer-actions-menu"
            transition="scale-transition"
            origin="top right"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                color="black"
                class="viewer-actions__dots-btn"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <div class="shared-viewer-actions-list">
              <v-btn
                text
                block
                small
                class="shared-viewer-actions-list__btn"
                @click="downloadPdf"
              >
                <v-icon left small>mdi-download</v-icon>
                Descargar
              </v-btn>
              <v-btn
                text
                block
                small
                class="shared-viewer-actions-list__btn"
                @click="shareChart"
              >
                <v-icon left small>mdi-share</v-icon>
                Compartir
              </v-btn>
            </div>
          </v-menu>
        </div>
        <v-overlay
          :value="threeDotsMenuOpen"
          :opacity="0.35"
          :absolute="true"
          @click.native="threeDotsMenuOpen = false"
        />
      </div>
    </div>

    <div
      v-if="!pluviometricChartId"
      class="shared-no-data"
    >
      <p>No se encontró la carta pluviométrica.</p>
    </div>
    <div
      v-else-if="$apollo.queries.pluviometricChartData.loading"
      class="shared-loading"
    >
      <v-progress-circular
        indeterminate
        color="#0098E1"
      />
    </div>
    <div
      v-else-if="pluviometricChartId && !pluviometricChartData"
      class="shared-no-data"
    >
      <p>No se encontró la carta pluviométrica o no está publicada.</p>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import PdfViewer from '../Shared/PdfViewer.vue'
import { parsePhoneNumberForWaMe } from '@/lib/helpers'

export default {
  name: 'SharedPluviometricChart',
  components: {
    PdfViewer
  },
  data () {
    return {
      pluviometricChartId: this.$route && this.$route.params ? this.$route.params.pluviometricChartId : null,
      pluviometricChartData: null,
      hamburgerMenuOpen: false,
      threeDotsMenuOpen: false
    }
  },
  apollo: {
    $client: 'auditsClient',
    pluviometricChartData () {
      return {
        query: gql`query getPluviometricChart($pluviometricChartId: String!) {
          pluviometricCharts(
            where: {
              sharingId: { equals: $pluviometricChartId },
              isPublished: { equals: true }
            }
          ){
            isPublished
            device {
              id
              name
            }
            file {
              url
            },
            sharingUrl
          }
        }`,
        update: data => {
          return data.pluviometricCharts && data.pluviometricCharts.length > 0 ? data.pluviometricCharts[0] : null
        },
        skip () {
          return !this.pluviometricChartId
        },
        variables () {
          return {
            pluviometricChartId: this.pluviometricChartId
          }
        }
      }
    }
  },
  methods: {
    downloadPdf () {
      if (this.pluviometricChartData && this.pluviometricChartData.file && this.pluviometricChartData.file.url) {
        window.open(this.pluviometricChartData.file.url, '_blank')
      }
      this.threeDotsMenuOpen = false
    },
    shareChart () {
      const url = this.pluviometricChartData && this.pluviometricChartData.sharingUrl
      if (!url) return
      const text = `Te quiero compartir esta carta de riego elaborada por PONCE. Accede mediante este link: ${url}`
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank')
      this.threeDotsMenuOpen = false
    },
    openAboutAudits () {
      this.hamburgerMenuOpen = false
      window.open('https://info.ponceag.com/auditorias', '_blank')
    },
    openAboutMonitoring () {
      this.hamburgerMenuOpen = false
      window.open('https://info.ponceag.com/monitoreo-riego-agricola', '_blank')
    },
    openContactSupport () {
      this.hamburgerMenuOpen = false
      const supportPhone = process.env.VUE_APP_SUPPORT_PHONE
      const phoneNumber = parsePhoneNumberForWaMe(supportPhone)
      window.open(`https://wa.me/${phoneNumber}`, '_blank')
    }
  }
}
</script>

<style scoped>
.shared-pluviometric-chart {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;
}

/* Top toolbar */
.shared-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: white;
  flex-shrink: 0;
  z-index: 10;
}

.shared-toolbar__logo {
  display: flex;
  align-items: center;
  gap: 8px;
}

.shared-toolbar__logo-icon {
  width: 36px;
  height: 36px;
  background-color: #0098E1;
  color: white;
  font-weight: 700;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.shared-toolbar__logo-text {
  font-weight: 700;
  font-size: 18px;
  color: #00202F;
}

.shared-toolbar__menu-btn {
  color: #000 !important;
}

.hamburger-overlay {
  background: transparent;
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  align-items: end;
  gap: 15px;
}

/* Pill design for menu buttons */
.hamburger-overlay__item {
  justify-content: flex-start !important;
  text-transform: none;
  margin: 0;
  color: #212121 !important;
  padding: 5px 20px 5px 10px !important;
  background: white !important;
  border-radius: 9999px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.hamburger-overlay__item:hover {
  background: #f5f5f5 !important;
}

/* White panel with PDF viewer */
.shared-viewer-panel {
  flex: 1;
  padding: 24px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: #eef2f5;
  position: relative;
}

.shared-viewer-area {
  position: relative;
  width: 100%;
  max-width: 900px;
  background: white;
  overflow: hidden;
  min-height: 500px;
}

/* 3-dots button over the PDF viewer */
.viewer-actions {
  position: absolute;
  top: 12px;
  right: 30px;
  z-index: 6;
}

.viewer-actions__dots-btn {
  background: rgba(255, 255, 255, 0.9) !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
}

.viewer-actions__dots-btn:hover {
  background: white !important;
}

.shared-no-data,
.shared-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: #757575;
}

/* Make PdfViewer fill the white panel (no extra padding) */
.shared-viewer-area ::v-deep .pluviometric-chart-container {
  padding: 0;
  max-width: none;
  min-height: 500px;
}

.shared-viewer-area ::v-deep .pdf-wrapper {
  border-radius: 0 0 8px 8px;
}

.shared-viewer-actions-menu {
  box-shadow: none !important;
  border-radius: 8px;
  overflow: hidden;
}

.shared-viewer-actions-list {
  padding: 4px 0;
  background: transparent !important;
}

.shared-viewer-actions-list__btn {
  justify-content: flex-start !important;
  text-transform: none;
  margin: 0;
  color: #212121 !important;
  padding: 0px 10px !important;
  background: white !important;
  border-radius: 9999px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 10px;
}
</style>

<style>
/* Hamburger v-menu: above v-overlay backdrop */
.hamburger-menu-content {
  background: transparent !important;
  box-shadow: none !important;
  z-index: 9;
}
</style>



// WEBPACK FOOTER //
// SharedPluviometricChart.vue?818b2c18